import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { map } from 'utilities/object'
import { first, last } from 'utilities/array'
import { read, utils } from 'xlsx'
import { Icon, Content, Heading, Flaps, FlapContent, Matrix } from './s'
import { Lock } from 'styled-icons/feather'
import Form from 'components/form/controller'
import Message from 'components/message'
import StringField from 'components/form/field/string'
import Actions from 'components/form/actions'
import { ButtonSubmit } from 'components/button'
import 'blob-polyfill'

const Spreadsheet = ({ raw, salt }) => {
    const { formatMessage } = useIntl()

    const [buffer, setBuffer] = useState(null)
    const [showPasswordPrompt, setShowPasswordPrompt] = useState(null)
    const [password, setPassword] = useState(null)
    const [wrongPassword, setWrongPassword] = useState(false)
    const [sheets, setSheets] = useState(null)

    useEffect(() => {
        (async () => {
            const buffer = await raw.arrayBuffer()
            setBuffer(buffer)
        })()
    }, [raw])

    useEffect(() => {
        if(buffer) {
            let workbook
            let success = true

            try {
                workbook = read(buffer, {
                    type: 'buffer',
                    WTF: true,
                    ...(password ? { password } : null)
                })
            } catch(error) {
                success = false

                const passwordProtected = error?.message === 'File is password-protected'
                const unsupportedProtection = error?.message === 'Unsupported password protection'

                if(passwordProtected || unsupportedProtection) {
                    setShowPasswordPrompt(unsupportedProtection ?
                        'disabled' :
                        'enabled'
                    )

                    !!password && setWrongPassword(true)
                }
            }

            if(!success) {
                return void setSheets(null)
            }

            const sheets = map(workbook.Sheets, (Sheet, label) => {
                const rows = getRows(Sheet)

                return {
                    label,
                    content: (
                        <>
                            <Heading className="sticky">{label}</Heading>
                            <Matrix
                                rows={rows}
                                salt={salt} />
                        </>
                    )
                }
            })

            setSheets(sheets)
        }
    }, [buffer, password])

    return (
        <Content className="spreadsheet">
            {!!showPasswordPrompt && (
                <Form
                    onSubmit={({ password }) => setPassword(password)}
                    layout="vertical">
                    {({ trigger }) => (
                        <>
                            <Icon>
                                <Lock size={24} />
                            </Icon>
                            {(showPasswordPrompt === 'enabled') && (
                                <>
                                    <Message
                                        type="info"
                                        message={formatMessage({
                                            id: 'document_preview_spreadsheet_information_password_protected',
                                            defaultMessage: 'This spreadsheet is password protected. Huma supports some password protection methods, but not all. If unlocking fails, please download the file and open it in a spreadsheet application.'
                                        })} />
                                    <StringField
                                        label={formatMessage({
                                            id: 'noun_password',
                                            defaultMessage: 'Password'
                                        })}
                                        name="password"
                                        field={{ required: true }}
                                        controlProps={{
                                            type: 'password',
                                            autoFocus: true
                                        }}
                                        {...(wrongPassword ? {
                                            error: formatMessage({
                                                id: 'document_preview_spreadsheet_error_password_wrong',
                                                defaultMessage: 'Wrong password or unsupported protection method'
                                            })
                                        } : null)}
                                        onChange={() => setWrongPassword(false)}
                                        salt={salt} />
                                    <Actions className="compact stretch">
                                        <ButtonSubmit ref={trigger}>
                                            <FormattedMessage
                                                id="action_unlock"
                                                defaultMessage="Unlock" />
                                        </ButtonSubmit>
                                    </Actions>
                                </>
                            )}
                            {(showPasswordPrompt === 'disabled') && (
                                <Message
                                    type="info"
                                    className="compact"
                                    message={formatMessage({
                                        id: 'document_preview_spreadsheet_information_password_protected_unsupported',
                                        defaultMessage: 'This spreadsheet is password protected with an unsupported method. Please download the file and open it in a spreadsheet application.'
                                    })} />
                            )}
                        </>
                    )}
                </Form>
            )}
            {!!sheets?.length && (
                <Flaps
                    items={sheets}
                    contentWrapper={FlapContent}
                    salt={salt} />
            )}
        </Content>
    )
}

export default Spreadsheet

const isRowEmpty = (row, now) => {
    if(Array.isArray(row)) {
        return !row
            .map(value => {
                if(value === now) {
                    return ''
                }

                return value
            })
            .join('')
    }

    // It’s probably `undefined`, and since we don’t want the while
    // loop to run forever, we just say it’s not empty and move on
    return false
}

const isCellEmpty = (cell, now) => cell === now

const getRows = sheet => {
    const now = Date.now()

    const json = utils.sheet_to_json(sheet, {
        raw: false,
        header: 1,
        defval: now
    })

    if(!json.length) {
        return json
    }

    // Max 1 consecutive empty row. Also remove leading empty rows.
    const { rows } = json.reduceRight(({ rows, previousRowEmpty, done }, row) => {
        const empty = isRowEmpty(row, now)

        if(empty && previousRowEmpty && !done) {
            return {
                rows,
                previousRowEmpty: true,
                done: false
            }
        }

        return {
            rows: [...rows, row],
            previousRowEmpty: empty,
            done: true
        }
    }, {
        rows: [],
        previousRowEmpty: true,
        done: false
    })

    while(isRowEmpty(first(rows), now)) {
        rows.unshift()
    }

    while(isRowEmpty(last(rows), now)) {
        rows.pop()
    }

    return rows
        .reverse()
        .map(row => {
            const { cells } = row.reduceRight(({ cells, previousCellEmpty, done }, cell) => {
                const empty = isCellEmpty(cell, now)

                if(empty && previousCellEmpty && !done) {
                    return {
                        cells,
                        previousCellEmpty: true,
                        done: false
                    }
                }

                if(empty) {
                    cell = ''
                }

                return {
                    cells: [...cells, cell],
                    previousCellEmpty: empty,
                    done: true
                }
            }, {
                cells: [],
                previousCellEmpty: true,
                done: false
            })

        return cells.reverse()
    })
}